import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Clock, Mail, ChevronDown, ChevronRight, Timer } from "lucide-react";
import { tripsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface TripScheduleControlsProps {
  tripId: string;
  active: boolean;
  checkIntervalHours: number;
  emailFrequency?: string;
  /**
   * True once the travel has happened. A watch switch on a finished trip is a
   * control that cannot do anything — the scheduler has always filtered
   * `departure_date >= CURRENT_DATE` — so it is shown as ended rather than
   * offered as a toggle that quietly no-ops.
   */
  past?: boolean;
  /** "YYYY-MM-DD" the travel finished, for the explanation line. */
  travelEndDate?: string | null;
}

const FREQUENCY_OPTIONS = [
  { hours: 6, label: "Every 6h" },
  { hours: 8, label: "3x daily" },
  { hours: 12, label: "2x daily" },
  { hours: 24, label: "Daily" },
];

const EMAIL_OPTIONS = [
  { value: "always", label: "Always", description: "Email after every rep" },
  { value: "improvement_only", label: "Improvement Only", description: "Only when Focus Set improves" },
];

function getFrequencyLabel(hours: number): string {
  const opt = FREQUENCY_OPTIONS.find((o) => o.hours === hours);
  return opt ? opt.label : hours + "h";
}

function getEmailLabel(freq: string): string {
  const opt = EMAIL_OPTIONS.find((o) => o.value === freq);
  return opt ? opt.label : freq;
}

export default function TripScheduleControls({
  tripId,
  active,
  checkIntervalHours,
  emailFrequency = "always",
  past = false,
  travelEndDate = null,
}: TripScheduleControlsProps) {
  const [expanded, setExpanded] = useState(false);
  const queryClient = useQueryClient();

  const toggleMutation = useMutation({
    mutationFn: (newActive: boolean) => tripsApi.update(tripId, { active: newActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trip", tripId] });
      queryClient.invalidateQueries({ queryKey: ["trips"] });
    },
  });

  const frequencyMutation = useMutation({
    mutationFn: (hours: number) => tripsApi.update(tripId, { check_interval_hours: hours } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trip", tripId] });
    },
  });

  const emailMutation = useMutation({
    mutationFn: (frequency: string) => tripsApi.update(tripId, { email_frequency: frequency } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trip", tripId] });
    },
  });

  return (
    <div className="rounded-md border border-border bg-muted/30">
      {/* Collapsed summary line */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 w-full px-4 py-2.5 text-left hover:bg-muted/50 transition-colors"
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
        )}
        <Timer className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="text-sm">
          {/* Finished travel is not "Paused" — nothing was paused, the trip
              happened. See tripStatusView for the one rule. */}
          <span className={past ? "text-muted-foreground" : active ? "text-emerald-600" : "text-red-600"}>
            {past ? "Watch ended" : active ? "Active" : "Paused"}
          </span>
          <span className="text-muted-foreground"> &middot; </span>
          <span className="text-muted-foreground">{getFrequencyLabel(checkIntervalHours)}</span>
          <span className="text-muted-foreground"> &middot; Email: </span>
          <span className="text-muted-foreground">{getEmailLabel(emailFrequency)}</span>
        </span>
      </button>

      {/* Expanded controls */}
      {expanded && (
        <div className="flex flex-col gap-3 px-4 pb-3 pt-1 border-t border-border/50">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">Status:</span>
            {past ? (
              <span className="text-sm text-muted-foreground">
                <Badge className="mr-2 bg-muted text-muted-foreground border-border">Past</Badge>
                {travelEndDate
                  ? "Travel finished " + travelEndDate + " — we stopped watching fares then."
                  : "This travel has happened — we stopped watching fares when it did."}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => toggleMutation.mutate(!active)}
                disabled={toggleMutation.isPending}
              >
                <Badge
                  className={
                    active
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200 cursor-pointer hover:bg-emerald-100"
                      : "bg-red-50 text-red-700 border-red-200 cursor-pointer hover:bg-red-100"
                  }
                >
                  {active ? "Active" : "Paused"}
                </Badge>
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Frequency:</span>
            {FREQUENCY_OPTIONS.map((opt) => (
              <Button
                key={opt.hours}
                variant={checkIntervalHours === opt.hours ? "default" : "outline"}
                size="sm"
                className="text-xs h-6 px-2"
                disabled={frequencyMutation.isPending}
                onClick={() => frequencyMutation.mutate(opt.hours)}
              >
                {opt.label}
              </Button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Mail className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Email:</span>
            {EMAIL_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                variant={emailFrequency === opt.value ? "default" : "outline"}
                size="sm"
                className="text-xs h-6 px-2"
                disabled={emailMutation.isPending}
                onClick={() => emailMutation.mutate(opt.value)}
                title={opt.description}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
